

export interface CursorUser {
  'User ID': string;
  'Email': string;
  'Is Active': boolean;
  'Chat Total Applies': number;
  'Chat Total Accepts': number;
  'Chat Accepted Lines Added': number;
  'Chat Tabs Shown': number;
  'Tabs Accepted': number;
  'Edit Requests': number;
  'Ask Requests': number;
  'Agent Requests': number;
}

// Special constant to indicate "no cursor data" for team members
export const NO_CURSOR_DATA = -999;

export interface LeaderboardEntry {
  userId: string;
  email: string;
  chatTotalApplies: number;
  chatTotalAccepts: number;
  chatAcceptedLinesAdded: number;
  totalLinesAdded: number;
  chatTabsShown: number;
  tabsAccepted: number;
  editRequests: number;
  askRequests: number;
  agentRequests: number;
  cmdkRequests: number;
  /** Total cycle spend in dollars (from overallSpendCents when present; else on-demand only). Optional. */
  spendPerUser?: number;
  /** On-demand portion in dollars (charged on top of included allowance); from spendCents. Optional. */
  spendOnDemandPerUser?: number;
  cursorTimeMinutes?: number;
  efficiencyScore?: number;
  teamMemberName?: string;
}

export type MetricKey = keyof Omit<LeaderboardEntry, 'userId' | 'email'>;

export interface WeeklyData {
  week: string;
  entries: LeaderboardEntry[];
}

export interface AuthToken {
  token: string;
}

export interface CrossoverUserProfile {
  managerAvatar: {
    id: number;
  };
  email: string;
  firstName: string;
  lastName: string;
  printableName: string;
}

export interface UserTeam {
  teamId: number;
  teamName: string;
  teamOwnerId: number;
  teamOwnerName: string;
  managerId: number;
  managerName: string;
  role: 'MEMBER' | 'MANAGER';
}

export interface UserTeamMembership {
  userId: number;
  email: string;
  name: string;
  teams: UserTeam[];
  isManager: boolean;
  hasCentralPSAccess: boolean;
  /** True when CPS access comes from the backend allowlist, not from CPS team roster (use CPS owner id for filtering). */
  centralPSAccessViaAllowlist?: boolean;
}

export interface CrossoverApplication {
  applicationName: string;
  applicationId: number;
  categoryColor: string;
  activityLevel: string;
  spentTime: number;
}

export interface CrossoverGroup {
  sectionName: string;
  color: string;
  groupItems: CrossoverApplication[];
  spentTime: number;
}

export interface CrossoverGrouping {
  periodLong: number;
  totalTrackedTime: number;
  alignmentScore: number;
  advancedGroups: CrossoverGroup[];
  focusScore: number;
  intensityScore: number;
}

export interface CrossoverCandidate {
  type: string;
  id: number;
  email: string;
  printableName: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
}

export interface CrossoverAssignmentHistoryItem {
  id: number;
  candidate: {
    id: number;
    printableName: string;
    firstName: string;
    lastName: string;
    email: string; // Company email - this is the primary matching field
  };
  assignedOn: string;
  releaseDate?: string;
  jobTitle: string;
  status: string;
}

export interface CrossoverAssignment {
  candidate: CrossoverCandidate;
  jobTitle: string;
  status: string;
  assignmentHistory?: CrossoverAssignmentHistoryItem[]; // Contains company email for matching
}

export interface CrossoverTeamMemberActivity {
  grouping: CrossoverGrouping;
  assignment: CrossoverAssignment;
}

export type CrossoverTeamActivityResponse = CrossoverTeamMemberActivity[];

export interface PersonalizedLeaderboardEntry extends LeaderboardEntry {
  cursorTimeMinutes: number;
  efficiencyScore: number;
  teamMemberName: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  managerId: number | null;
  userProfile: CrossoverUserProfile | null;
  teamMembership: UserTeamMembership | null;
  loading: boolean;
  error: string | null;
}

export interface DailyUsageData {
  date: number;
  email: string;
  isActive: boolean;
  totalLinesAdded: number;
  totalLinesDeleted: number;
  acceptedLinesAdded: number;
  acceptedLinesDeleted: number;
  totalApplies: number;
  totalAccepts: number;
  totalRejects: number;
  totalTabsShown: number;
  totalTabsAccepted: number;
  composerRequests: number;
  chatRequests: number;
  agentRequests: number;
  cmdkUsages: number;
  subscriptionIncludedReqs: number;
  apiKeyReqs: number;
  usageBasedReqs: number;
  bugbotUsages: number;
  mostUsedModel: string;
  tabMostUsedExtension: string;
}

export interface DailyUsageApiResponse {
  period: {
    startDate: number;
    endDate: number;
  };
  data: DailyUsageData[];
}

export interface DailyUsageApiRequest {
  startDate: number;
  endDate: number;
}



// AI Overview Data Types
export interface AIApplication {
  applicationName: string;
  applicationId: number;
  spentTime: number;
  sectionName: 'AI' | 'PURE_AI';
  categoryColor: string;
  activityLevel: string;
}

export interface AIOverviewData {
  teamMemberName: string;
  email: string;
  aiApplications: AIApplication[];
  totalAITime: number;
  totalPureAITime: number;
  totalCombinedAITime: number;
}

export interface ProcessedAIData {
  users: AIOverviewData[];
  totalUsers: number;
  overallAITime: number;
  overallPureAITime: number;
  mostUsedAIApps: Array<{
    applicationName: string;
    totalTime: number;
    userCount: number;
  }>;
}

// Non-AI Applications Data Types
export interface ApplicationUsage {
  applicationName: string;
  totalUsers: number;
  totalHours: number;
  category: string;
  percentage: number;
}

export interface TeamMemberApplications {
  userId: string;
  email: string;
  name: string;
  applications: {
    [appName: string]: {
      hours: number;
      category: string;
      frequency: number;
    };
  };
  totalNonAIHours: number;
  topApplication: string;
  applicationsByCategory: {
    [category: string]: { appName: string; hours: number }[];
  };
}

export interface NonAIDataResult {
  teamMemberApplications: TeamMemberApplications[];
  topApplications: ApplicationUsage[];
}

// Tab interface types
export type DataMode = 'basic' | 'central-ps' | 'my-team';
export type ViewMode = 'cursor' | 'ai-overview' | 'non-ai-apps' | 'team-ai' | 'worksmart-ai';

export interface TabState {
  dataMode: DataMode;
  viewMode: ViewMode;
} 